<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-12">
          <h5>Lesson Plan Overview Report</h5>
        </div>
      </div>
    </div>

    <div class="row justify-content-center" v-if="pagepermission.viewable">
      <div class="col-xl-12">
        <!-- Summary KPI Cards -->
        <div class="row mb-3">
          <div class="col-md-4 col-12 mb-2">
            <div class="card card-body bg-light p-3 border-0 shadow-sm rounded">
              <div class="text-uppercase text-muted font-size-12 font-weight-bold">Total Lesson Plans</div>
              <div class="font-size-24 font-weight-bold text-dark mt-1">{{ meta.total || data.length }}</div>
            </div>
          </div>
          <div class="col-md-4 col-12 mb-2">
            <div class="card card-body bg-light p-3 border-0 shadow-sm rounded">
              <div class="text-uppercase text-muted font-size-12 font-weight-bold">Average Progress</div>
              <div class="font-size-24 font-weight-bold text-primary mt-1">{{ avgProgress }}%</div>
            </div>
          </div>
          <div class="col-md-4 col-12 mb-2">
            <div class="card card-body bg-light p-3 border-0 shadow-sm rounded">
              <div class="text-uppercase text-muted font-size-12 font-weight-bold">Lagging Plans / Sections</div>
              <div class="font-size-24 font-weight-bold text-danger mt-1">{{ laggingCount }}</div>
            </div>
          </div>
        </div>

        <div class="row align-items-center mb-2">

          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              placeholder="Select Session"
              style="width: 100%"
              @change="handleSessionChange"
              :defaultValue="currentSession ? currentSession.id : undefined"
            >
              <template v-for="schoolSession in schoolSessions">
                <a-select-option :value="schoolSession.id" :key="schoolSession.id">
                    {{schoolSession.name}}
                    <small class="text-primary ml-2" v-if="currentSession && currentSession.id === schoolSession.id">Current</small>
                </a-select-option>
              </template>
            </a-select>
          </div>

          <div class="col-md-3 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                style="width: 100%"
                @change="handleClassSelect"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">All Classes</a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                    {{schoolClass.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>

          <div class="col-md-3 col-12 mb-2">
            <a-spin :spinning="subjectSpinning">
              <a-select
                size="large"
                v-model="selectedSubject"
                placeholder="All Subjects"
                style="width: 100%"
                @change="handleSubjectChange"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">All Subjects</a-select-option>
                <template v-for="subject in subjects">
                  <a-select-option :value="subject.id" :key="subject.id">
                    {{subject.name}}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>

          <div class="col-md-3 col-12 mb-2">
             </div>

        </div>

        <div class="card">
          <div class="card-body">
            <lesson-plan-overview-table
              :data="data"
              :loading="loading"
              @updateTable="handleUpdateTable"
              @viewDetails="handleViewDetails"
            />
          </div>
        </div>

        <div class="text-right">
          <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="meta.total"
            :pageSize.sync="pageSize"
            v-model="current"
            @change="onChange"
          />
        </div>
      </div>
    </div>

    <div class="row justify-content-center" v-else>
      <a-spin :spinning="permissionCheckSpinning">
        <p class="error" v-if="!permissionCheckSpinning">
          You don't have permission to view this report.
        </p>
      </a-spin>
    </div>
  </div>
</template>

<script>
import { find } from 'lodash'
import LessonPlanOverviewTable from '@/components/lessonplan/report/overviewReport'

export default {
  components: {
    LessonPlanOverviewTable,
  },

  data: function() {
    return {
      loading: true,
      data: [],
      meta: { total: 0 },
      schoolSessions: [],
      schoolClasses: [],
      subjects: [],
      selectedSession: undefined,
      selectedClass: undefined,
      selectedSubject: undefined,
      classSpinning: false,
      subjectSpinning: false,
      permissionCheckSpinning: true,
      current: 1,
      pageSize: 10,
      sorterField: null,
      sorterOrder: null,
      filters: {},
      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
      },
    }
  },

  computed: {
    currentSession: function() {
      return find(this.schoolSessions, ['is_current', 1])
    },
    avgProgress() {
      if (!this.data || this.data.length === 0) return 0
      const sum = this.data.reduce((acc, curr) => acc + (curr.progress || 0), 0)
      return Math.round(sum / this.data.length)
    },
    laggingCount() {
      if (!this.data) return 0
      return this.data.filter(item => {
        if (!item.sections_lagging) return false
        const count = parseInt(item.sections_lagging.split('/')[0])
        return count > 0
      }).length
    },
  },

  mounted: function() {
    this.$roleutility.checkpermission('lessonplan', 23)
      .then(data => {
        this.pagepermission = data.pagePermission
        this.permissionCheckSpinning = false
        if (this.pagepermission.viewable) {
          this.getSessionsAndInitialLoad()
        }
      })
      .catch(() => {
        this.permissionCheckSpinning = false
        this.pagepermission.viewable = false
      })
  },

  methods: {
    // Fetches sessions, sets the current one, and triggers other loads
    getSessionsAndInitialLoad() {
      this.$http.get('/api/session')
        .then(({ data }) => {
          this.schoolSessions = data.data
          const currentSession = this.currentSession
          this.selectedSession = currentSession ? currentSession.id : (data.data.length > 0 ? data.data[0].id : undefined)

          if (this.selectedSession) {
            this.getClasses()
            this.getSubjects()
            this.getLessonPlans()
          }
        })
        .catch(error => {
          console.error('Error fetching sessions:', error)
          this.$neoNotify.error(error)
        })
    },

    // Main API call for the report data
    getLessonPlans(isFilterOrSearch = false) {
      this.loading = true
      let page = this.current
      if (isFilterOrSearch) {
        page = 1
        this.current = 1
      }

      this.$http.get('/api/lesson-plan/reports/overview', {
        params: {
          limit: this.pageSize,
          page: page,
          class_id: this.selectedClass,
          subject_id: this.selectedSubject,
          session_id: this.selectedSession,
          sorter_field: this.sorterField,
          sorter_order: this.sorterOrder,
          ...this.filters,
        },
      }).then(({ data }) => {
        this.loading = false
        this.data = data.data
        this.meta = data.meta
      }).catch(error => {
        this.loading = false
        console.error('Error fetching lesson plans:', error)
        this.$neoNotify.error(error)
      })
    },

    getClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', { params: { session_id: this.selectedSession } })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch(error => {
          this.classSpinning = false
          console.error('Error fetching classes:', error)
        })
    },

    getSubjects() {
      this.subjectSpinning = true
      this.$http.get('/api/subject', {
        params: {
          session_id: this.selectedSession,
          class_id: this.selectedClass, // Often subjects are filtered by class
        },
      })
        .then(({ data }) => {
          this.subjects = data.data
          this.subjectSpinning = false
        })
        .catch(error => {
          this.subjectSpinning = false
          console.error('Error fetching subjects:', error)
        })
    },

    // --- Filter Handlers ---
    handleClassSelect(value) {
      this.selectedClass = value
      this.selectedSubject = undefined // Reset subject when class changes
      this.getSubjects()
      this.getLessonPlans(true)
    },

    handleSubjectChange(value) {
      this.selectedSubject = value
      this.getLessonPlans(true)
    },

    handleSessionChange (value) {
      this.selectedSession = value
      this.selectedClass = undefined
      this.selectedSubject = undefined

      this.getClasses()
      this.getSubjects()
      this.getLessonPlans(true)
    },

    // --- Table/Pagination Handlers ---
    handleUpdateTable(args) {
      if (args) {
        this.sorterField = args[0]
        this.sorterOrder = args[1]
        this.filters = args[2]
      }
      this.getLessonPlans()
    },

    handleViewDetails(record) {
      this.$router.push({ name: 'lessonplan-details', params: { plan_id: record.id } })
    },

    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.getLessonPlans()
    },

    onChange(current) {
      this.current = current
      this.getLessonPlans()
    },
  },
}
</script>

<style scoped>
.error { padding: 25px 5px; }
</style>
